import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check, Loader2, X } from "lucide-react";

export function Field({ label, required, hint, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-sm font-medium">{label}{required && <span className="text-red-600"> *</span>}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-[#5C736A] mt-1">{hint}</p>}
    </div>
  );
}

export function SearchableInput({ id, options, value, onChange, placeholder, testid }) {
  return (
    <>
      <Input list={id} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="h-12" autoComplete="off" data-testid={testid} />
      <datalist id={id}>{options.map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
}

export function PasswordInput({ value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-12 pr-12" placeholder={placeholder} autoComplete="new-password" data-testid={testid} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-[#5C736A] hover:text-[#1A2E28]"
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"} data-testid={`${testid}-toggle`}>
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
}

export function UsernameInput({ value, onChange, onStatus }) {
  const [state, setState] = useState({ checking: false, available: null, reason: null });
  useEffect(() => {
    if (!value || value.length < 3) { setState({ checking: false, available: null }); onStatus(null); return undefined; }
    setState((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/username-available", { params: { username: value } });
        setState({ checking: false, available: data.available, reason: data.reason });
        onStatus(data.available);
      } catch { setState({ checking: false, available: null }); onStatus(null); }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div>
      <div className="relative">
        <Input value={value} onChange={(e) => onChange(e.target.value.replace(/\s/g, ""))} className="h-12 pr-10"
          placeholder="ex : mdiallo.ecole" autoComplete="username" data-testid="reg-username" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          {state.checking && <Loader2 className="w-4 h-4 animate-spin text-[#5C736A]" />}
          {!state.checking && state.available === true && <Check className="w-4 h-4 text-[#1F7A63]" />}
          {!state.checking && state.available === false && <X className="w-4 h-4 text-red-600" />}
        </span>
      </div>
      {!state.checking && state.available === false && (
        <p className="text-xs text-red-600 mt-1" data-testid="reg-username-taken">{state.reason || "Cet identifiant est déjà utilisé."}</p>
      )}
      {!state.checking && state.available === true && <p className="text-xs text-[#1F7A63] mt-1" data-testid="reg-username-ok">Identifiant disponible</p>}
    </div>
  );
}

export function Stepper({ steps, current }) {
  return (
    <ol className="flex items-center gap-2 mb-8" data-testid="reg-stepper">
      {steps.map((s, i) => (
        <li key={s} className="flex-1 min-w-0">
          <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= current ? "bg-[#1F7A63]" : "bg-[#E2E8F0]"}`} />
          <div className={`mt-2 text-[11px] sm:text-xs font-medium truncate ${i === current ? "text-[#1F7A63]" : "text-[#5C736A]"}`}>
            <span className="hidden sm:inline">{i + 1}. </span>{s}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Recap({ rows }) {
  return (
    <dl className="divide-y divide-[#E2E8F0] rounded-xl border border-[#E2E8F0] bg-white" data-testid="reg-recap">
      {rows.filter(([, v]) => v !== undefined).map(([k, v]) => (
        <div key={k} className="grid grid-cols-5 gap-3 px-4 py-2.5 text-sm">
          <dt className="col-span-2 text-[#5C736A]">{k}</dt>
          <dd className="col-span-3 font-medium break-words">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function validPassword(p) {
  return p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);
}

export async function compressLogo(file) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  });
  const max = 320;
  const ratio = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * ratio);
  c.height = Math.round(img.height * ratio);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/png");
}
